/**
 * Response envelope of POST /update_ipd_prescription_status/.
 *
 * A 200 "Success" can still decline rows: the envelope carries
 * updated_ids / noop_ids / rejected[], and each rejected entry has a
 * user-facing `reason` (backend can_transition writes sentences on purpose).
 * Callers must read rejected — the doctor console's habit of surfacing only
 * rejected[0] hid multi-row outcomes, so this helper reports all of them.
 *
 * Import-free so it stays unit-testable (axios ESM breaks Jest).
 */

export function statusUpdateOutcome(res) {
  const updated = Array.isArray(res?.updated_ids) ? res.updated_ids : [];
  const noop = Array.isArray(res?.noop_ids) ? res.noop_ids : [];
  const rejected = Array.isArray(res?.rejected) ? res.rejected : [];
  return {
    updated,
    noop,
    rejected,
    // A pure no-op is success too — the rows are already where the user asked;
    // treating it as an error (like MedicationOrdersPanel does) reads as failure.
    ok: updated.length > 0 || (noop.length > 0 && rejected.length === 0),
  };
}

/** One sentence listing every declined row's reason, or '' when none. */
export function rejectedReasonsText(rejected) {
  const rows = Array.isArray(rejected) ? rejected : [];
  if (!rows.length) return '';
  const reasons = rows.map((row) => {
    const reason = row?.reason || 'rejected';
    return row?.id != null ? `#${row.id}: ${reason}` : reason;
  });
  return `${rows.length} row(s) declined — ${reasons.join('; ')}`;
}

// ─── Bulk short-close grouping ──────────────────────────────────────────────

/**
 * Why a line cannot be supplied, inferred from what the stock check already
 * found. These are exact members of PHARMACY_SHORT_CLOSE_REASONS (constants) —
 * the backend requires a reason from its own list, so a free-text guess here
 * would come back rejected.
 *
 * Kept as a map rather than a conditional so an unrecognised stock status falls
 * through to no auto-reason and the pharmacist picks one, instead of being
 * silently filed under the wrong cause.
 */
const REASON_BY_STOCK_STATUS = {
  OUT_OF_STOCK: 'Out of Stock',
  NOT_LISTED: 'Not in Formulary',
};

export function shortCloseReasonForStock(stockStatus) {
  return REASON_BY_STOCK_STATUS[String(stockStatus || '').toUpperCase()] || '';
}

/**
 * Which lines can be closed as "we cannot supply this", grouped by the reason
 * their stock status implies.
 *
 * Grouped rather than flat because POST /update_ipd_prescription_status/ takes
 * ONE reason for the whole `prescription_ids` array — out-of-stock and
 * not-in-formulary lines cannot ride the same call without one of them being
 * recorded under a cause that is not true. Two groups means two calls, still
 * one dialog and one confirmation.
 *
 * Only lines the backend can actually address are included: a synthetic id
 * ("rx-med-0", from mapPrescriptionItem when the row carried none) identifies
 * nothing server-side, and an emergency script has no admission to scope the
 * write to — both are left to the per-line path rather than silently dropped
 * from a bulk action that claims to have closed them.
 */
export function groupLinesForShortClose(lines, availability) {
  const groups = new Map();

  (Array.isArray(lines) ? lines : []).forEach((line) => {
    const id = String(line?.id ?? '').trim();
    if (!/^\d+$/.test(id)) return;
    const entry = availability?.[line.id];
    if (!entry || entry.loading) return;
    const reason = shortCloseReasonForStock(entry.status);
    if (!reason) return;

    const group = groups.get(reason) || { reason, defaultReason: reason, lines: [] };
    group.lines.push({ id: Number(id), medicine: line.medicine || '' });
    groups.set(reason, group);
  });

  return [...groups.values()];
}

/** Total lines a set of groups would close — the number the button promises. */
export function shortCloseLineCount(groups) {
  return (Array.isArray(groups) ? groups : [])
    .reduce((sum, group) => sum + (group?.lines?.length || 0), 0);
}

/**
 * Fold several per-group status-update responses into one outcome.
 *
 * The pharmacist confirmed ONE action, so they must be told about it once — a
 * toast per group would report the same decision two or three times. `ok` is
 * true only when every call that ran succeeded: a bulk action that closed the
 * out-of-stock lines but not the unlisted ones has not done what the button said.
 */
export function mergeStatusUpdateOutcomes(responses) {
  const list = Array.isArray(responses) ? responses : [];
  const merged = list.map(statusUpdateOutcome);
  return {
    updated: merged.flatMap((o) => o.updated),
    noop: merged.flatMap((o) => o.noop),
    rejected: merged.flatMap((o) => o.rejected),
    ok: merged.length > 0 && merged.every((o) => o.ok),
  };
}
